import React, { useState, useEffect } from "react";
import api from "../api/api";
import { Container, Row, Col, Card } from "react-bootstrap";
import PageTitle from "../components/common/PageTitle";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import "moment/locale/nb";
import {
  TextField,
  Button,
  CardContent,
  Typography,
  Switch,
  LinearProgress,
} from "@mui/material";
import SimulationChart from "./SimulationChart";
import { useLocation } from "react-router";

const localizer = momentLocalizer(moment);

const CalculSimulation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const initialData = location.state ? location.state.calculData : null;
  const [calculData, setCalculData] = useState(initialData);

  const toHoursAndMinutes = (totalMinutes) => {
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);
    return `${hours}${t("hours")} ${parseFloat(minutes).toFixed(0)}${t(
      "minutes"
    )}`;
  };

  useEffect(() => {
    let data = [];
    calculData &&
      calculData.forEach((event) => {
        let startDate = new Date(event.start_date).toISOString().slice(0, 10);
        let endDate = new Date(event.end_date).toISOString().slice(0, 10);
        let resultCalcul = event.result;
        const filteredEvents = resultCalcul.filter((result) => {
          let resultDate = new Date(result.date).toISOString().slice(0, 10);
          return (
            result.irrigationNbr === 1 &&
            resultDate >= startDate &&
            resultDate < endDate
          );
        });
        filteredEvents &&
          filteredEvents.forEach((event) => {
            data.push({
              title: (
                <div style={{ fontSize: 11.5 }}>
                  <div>
                    {t("irrigation_dose") +
                      ": " +
                      parseFloat(event.irrigation).toFixed(2) +
                      " mm"}
                  </div>
                  <div>
                    {t("irrigation_time") +
                      ": " +
                      toHoursAndMinutes(event.irrigationTime)}
                  </div>
                  <div>
                    {t("watering_rainfall") +
                      ": " +
                      parseFloat(event.pluieArrosage).toFixed(2) +
                      " mm"}
                  </div>
                </div>
              ),
              allDay: true,
              start: new Date(event.date),
              end: new Date(event.date),
              source: "resultCalcul",
            });
          });
      });
    setEvents(data);
  }, [calculData]);

  const handleChange = (index, field, value) => {
    const newData = [...calculData];
    newData[index].inputs[0][field] = field === "plantingDate" ? value : Number(value);
    setCalculData(newData);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updatePromises = calculData.map(async (data) => {
        const { field_id, inputs } = data;
        const { RUmax, effPluie, effIrrig, irrigArea, ruPratique, profondeur } =
          inputs[0];
        const payload = {
          fieldId: field_id,
          RUmax,
          effPluie,
          effIrrig,
          irrigArea,
          ruPratique,
          profondeur,
        };
        const response = await api.post("/admin/edit-bilan-hydrique", payload);
        return response.data;
      });
      const updatedData = await Promise.all(updatePromises);
      if (updatedData && typeof updatedData !== "undefined") {
        setCalculData(updatedData[0].data);
        swal(t("success"), t("data_saved_success"), "success");
      }
    } catch (error) {
      console.error("Error saving data:", error);
      swal(t("error"), t("failed_to_save_data"), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    let dataDisplayed = [];
    let todayDate = new Date();
    let today = todayDate.toISOString().slice(0, 10);
    let filteredResult = [];
    calculData &&
      calculData.map((result) => {
        let resultData = result.result;
        let startDate = new Date(result.start_date).toISOString().slice(0, 10);
        let endDate = new Date(result.end_date).toISOString().slice(0, 10);
        if (today >= startDate && today <= endDate) {
          filteredResult = resultData.filter((result) => {
            let resultDate = new Date(result.date).toISOString().slice(0, 10);
            return startDate <= resultDate && endDate >= resultDate;
          });
        }
        if (resultData) {
          filteredResult.map((data) => {
            dataDisplayed.push({
              bilan: data.bilan,
              ETC: data.Etc,
              ET0: data.ET0,
              kc: data.kc,
              dates: data.date,
              RUmax: data.RUmax,
              RUmin: data.RUMin,
            });
          });
        }
      });
    setChartData(dataDisplayed);
  }, [calculData]);

  if (!calculData) {
    return <div>{t("no_data_available")}</div>;
  }

  return (
    <>
      <Container className="p-4">
        <Row noGutters className="page-header py-4">
          <PageTitle
            sm="4"
            title={t("water_balance_simulation")}
            className="text-sm-left"
          />
        </Row>
        <Row className="mb-3">
          <Col xs={12} className="d-flex justify-content-end">
            <Typography variant="body1" className="mr-2">
              {t("edit_mode")}
            </Typography>
            <Switch
              checked={isEditMode}
              onChange={() => setIsEditMode(!isEditMode)}
            />
          </Col>
        </Row>
        {calculData &&
          calculData.map((data, index) => {
            let inputs = data.inputs;
            return (
              <Card key={index} className="my-3">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("simulation")} { index + 1 }
                  </Typography>
                  <Row className="gap-2">
                    <Col xs={12} sm={6} md={3}>
                      <TextField
                        label={t("ru_pratique")}
                        value={inputs[0].ruPratique}
                        onChange={(e) =>
                          handleChange(index, "ruPratique", e.target.value)
                        }
                        fullWidth
                        margin="normal"
                        type="number"
                        InputProps={{ readOnly: !isEditMode }}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <TextField
                        label={t("ru_max")}
                        value={inputs[0].RUmax}
                        onChange={(e) =>
                          handleChange(index, "RUmax", e.target.value)
                        }
                        fullWidth
                        margin="normal"
                        type="number"
                        InputProps={{ readOnly: !isEditMode }}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <TextField
                        label={t("effective_pluie")}
                        value={inputs[0].effPluie}
                        onChange={(e) =>
                          handleChange(index, "effPluie", e.target.value)
                        }
                        fullWidth
                        margin="normal"
                        type="number"
                        InputProps={{ readOnly: !isEditMode }}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <TextField
                        label={t("effective_irrigation")}
                        value={inputs[0].effIrrig}
                        onChange={(e) =>
                          handleChange(index, "effIrrig", e.target.value)
                        }
                        fullWidth
                        margin="normal"
                        type="number"
                        InputProps={{ readOnly: !isEditMode }}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <TextField
                        label={t("irrigation_area")}
                        value={inputs[0].irrigArea}
                        onChange={(e) =>
                          handleChange(index, "irrigArea", e.target.value)
                        }
                        fullWidth
                        margin="normal"
                        type="number"
                        InputProps={{ readOnly: !isEditMode }}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <TextField
                        label={t("profondeur")}
                        value={inputs[0].profondeur}
                        onChange={(e) =>
                          handleChange(index, "profondeur", e.target.value)
                        }
                        fullWidth
                        margin="normal"
                        type="number"
                        InputProps={{ readOnly: !isEditMode }}
                      />
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <TextField
                        label={t("planting_date")}
                        value={inputs[0].plantingDate}
                        onChange={(e) =>
                          handleChange(index, "plantingDate", e.target.value)
                        }
                        fullWidth
                        margin="normal"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ readOnly: !isEditMode }}
                      />
                    </Col>
                  </Row>
                  <Row className="d-flex justify-content-center mt-4">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSave}
                      disabled={!isEditMode || isSaving}
                    >
                      {t("save_changes")}
                    </Button>
                  </Row>
                </CardContent>
              </Card>
            );
          })}
        {isSaving ? (
          <LinearProgress />
        ) : (
          <>
            <Row>
              <Col lg="12" md="12" sm="12" className="mb-4">
                <Card>
                  <Card.Body className="p-0">
                    <SimulationChart data={chartData} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col lg="12" md="12" sm="12">
                <Card>
                  <Card.Body>
                    <div style={{ height: 590 }}>
                      <Calendar
                        key={events.length}
                        localizer={localizer}
                        events={events}
                        culture="en-GB"
                        views={["month", "week", "day"]}
                        startAccessor="start"
                        endAccessor="end"
                        eventPropGetter={(event, start, end, isSelected) => ({
                          event,
                          start,
                          end,
                          isSelected,
                          style: { backgroundColor: "var(--sf-night-primary, #26A6B7)" },
                        })}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </>
  );
};

export default CalculSimulation;
